import dayjs from "dayjs";

import type { BacklinksTimeSeriesSummary } from "@similarweb/pro-api-client";

import {
    GRANULARITY_INDEX,
    DEFAULT_ITEM_INDEX_RANGE_FOR_IN_PROGRESS_PERIOD,
    BacklinksChartColors,
} from "app/pages/digital-marketing/backlinks/shared/BacklinksConsts";
import { sitesColors } from "app/common/campaigns/KeywordsCampaginKeywordsTableConsts";

export interface IGraphDataFormat {
    backlinks: number[][];
    referringDomains: number[][];
}

/**
 * Transforms backlinks granularity filtered data into chart-ready format
 * @param granularityFilteredData - Array of filtered data items
 * @returns Array of transformed data in IGraphDataFormat
 */
export const transformBacklinksData = (granularityFilteredData: BacklinksTimeSeriesSummary[][]): IGraphDataFormat[] => {
    if (!granularityFilteredData?.length) {
        return [];
    }

    return granularityFilteredData.map((granularityFilteredDataItem) => {
        return granularityFilteredDataItem.reduce(
            (acc, item) => {
                const date = dayjs.utc(item.Date).valueOf();

                const backlinksValue = Number(item.Backlinks);
                const refDomainsValue = Number(item.ReferringDomains);

                acc.backlinks.push([date, isNaN(backlinksValue) ? 0 : backlinksValue]);
                acc.referringDomains.push([date, isNaN(refDomainsValue) ? 0 : refDomainsValue]);

                return acc;
            },
            { backlinks: [], referringDomains: [] } as IGraphDataFormat,
        );
    });
};

interface IChartSeriesItem {
    name: string;
    id?: string;
    data: number[][];
    color: string;
    total?: number;
    visible: boolean;
    yAxis?: number;
    zoneAxis?: string;
    zones?: Array<{
        value?: number;
        dashStyle?: string;
    }>;
}

/**
 * Configuration for the selected tabs in compare mode for the Backlinks Overtime Chart.
 * This gurantees that the tabs are always and the data key are syncronized.
 */
export const compareModeSelectedTabsConfig: Array<{
    dataAutomation: string;
    tabNameKey: string;
    dataKey: keyof IGraphDataFormat;
}> = [
    {
        dataAutomation: "backlinks-referring-domains-tab",
        tabNameKey: "digitalsuite.backlinks.overview.refdomains_count.title",
        dataKey: "referringDomains",
    },
    {
        dataAutomation: "backlinks-backlinks-tab",
        tabNameKey: "digitalsuite.backlinks.overview.backlinks_count.title",
        dataKey: "backlinks",
    },
];

/**
 * Formats backlinks data into chart series format ready for the LineGraph component
 * @param calculatedData - Array of transformed backlinks data
 * @param isCompareMode - Whether the chart is in compare mode (multiple websites)
 * @param compareModeTab - Selected tab index in compare mode
 * @param granularityIndex - Selected granularity index
 * @param keysToFetch - Array of domain keys to display
 * @returns Array of formatted chart series items
 */
export const formatBacklinksChartData = (
    calculatedData: IGraphDataFormat[],
    isCompareMode: boolean,
    compareModeTab: number,
    granularityIndex: number,
    keysToFetch: Array<string | undefined>,
    visibleItems: Record<string, boolean> = {},
): IChartSeriesItem[] => {
    if (!calculatedData?.length) {
        return [];
    }

    if (isCompareMode) {
        const colors = sitesColors;
        const selectedField = compareModeSelectedTabsConfig[compareModeTab].dataKey;
        return calculatedData.map((item, index) => {
            const domain = keysToFetch[index];
            const total = item[selectedField].reduce((acc, dataItem) => acc + dataItem[1], 0);
            return {
                name: domain ?? "",
                id: domain,
                data: item[selectedField],
                color: colors[index],
                total,
                visible: visibleItems[domain as string] ?? true,
            };
        });
    }

    const primary = calculatedData[0];

    const backlinksIndexShortDot = primary.backlinks.length - DEFAULT_ITEM_INDEX_RANGE_FOR_IN_PROGRESS_PERIOD;
    const backlinksSeries = {
        color: BacklinksChartColors.BACKLINKS,
        name: "Backlinks",
        data: primary.backlinks,
        visible: true,
        yAxis: 0,
        zoneAxis: "x",
        zones:
            granularityIndex !== GRANULARITY_INDEX.DAILY && backlinksIndexShortDot >= 0
                ? [
                      {
                          value: primary.backlinks[backlinksIndexShortDot]?.[0] || 0,
                      },
                      {
                          dashStyle: "ShortDot",
                      },
                  ]
                : [],
    };

    const domainsIndexShortDot = primary.referringDomains.length - DEFAULT_ITEM_INDEX_RANGE_FOR_IN_PROGRESS_PERIOD;
    const referringDomainsSeries = {
        color: BacklinksChartColors.REFDOMAINS,
        name: "Referring Domains",
        data: primary.referringDomains,
        visible: true,
        yAxis: 1,
        zoneAxis: "x",
        zones:
            granularityIndex !== GRANULARITY_INDEX.DAILY && domainsIndexShortDot >= 0
                ? [
                      {
                          value: primary.referringDomains[domainsIndexShortDot]?.[0] || 0,
                      },
                      {
                          dashStyle: "ShortDot",
                      },
                  ]
                : [],
    };

    return [referringDomainsSeries, backlinksSeries];
};
